import { Email } from '../lib/api';
import { AlertCircle, Mail, Inbox, CheckCircle } from 'lucide-react';

interface TodoSidebarProps {
  emails: Email[];
  selectedEmail: Email | null;
  onSelectEmail: (email: Email) => void;
  isLoading: boolean;
}

interface TodoItem {
  id: string;
  title: string;
  source: string;
  icon: string;
  email: Email;
}

export default function TodoSidebar({
  emails,
  selectedEmail,
  onSelectEmail,
  isLoading,
}: TodoSidebarProps) {
  // Extract action items from emails that have actionRequired or high urgency
  const getTodoItems = (): TodoItem[] => {
    const items: TodoItem[] = [];
    
    emails.forEach((email) => {
      if (email.actionRequired || email.urgency === 'high') {
        // Extract source from email
        const source = extractSource(email.from);
        const title = email.suggestedAction || `Follow up: ${email.subject}`;
        
        items.push({
          id: email.id,
          title: title.substring(0, 50),
          source,
          icon: getSourceIcon(email.from),
          email,
        });
      }
    });
    
    return items.slice(0, 10); // Limit to 10 items
  };

  const todoItems = getTodoItems();

  return (
    <div className="flex flex-col h-full bg-white dark:bg-gray-800">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
          <AlertCircle className="w-5 h-5 text-orange-500" />
          Your To-dos
        </h2>
      </div>

      {/* Todo List */}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="space-y-2 p-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-16 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse" />
            ))}
          </div>
        ) : todoItems.length === 0 ? (
          <div className="flex-1 flex items-center justify-center p-4 text-center">
            <div>
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-2" />
              <p className="text-sm text-gray-500 dark:text-gray-400">
                No pending actions
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-2 p-3">
            {todoItems.map((item) => (
              <button
                key={item.id}
                onClick={() => onSelectEmail(item.email)}
                className={`w-full text-left p-3 rounded-lg transition-colors ${
                  selectedEmail?.id === item.id
                    ? 'bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800'
                    : 'hover:bg-gray-100 dark:hover:bg-gray-700 border border-transparent'
                }`}
              >
                <div className="flex items-start gap-3">
                  <span className="text-lg mt-0.5">{item.icon}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {item.title}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {item.source}
                    </p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Footer stats */}
      <div className="p-3 border-t border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-400">
          <span className="flex items-center gap-1">
            <Inbox className="w-4 h-4" />
            {emails.length} emails
          </span>
          <span className="flex items-center gap-1">
            <Mail className="w-4 h-4" />
            {emails.filter(e => !e.isRead).length} unread
          </span>
        </div>
      </div>
    </div>
  );
}

function extractSource(from: string): string {
  // Extract domain or name from email address
  const match = from.match(/(?:^|<)([^>@]+@)?([^>@.]+)/);
  if (match && match[2]) {
    const domain = match[2].toLowerCase();
    // Map common domains to friendly names
    const sourceMap: Record<string, string> = {
      'gmail': 'Gmail',
      'outlook': 'Outlook',
      'apple': 'Apple',
      'southeast': 'South East Water',
      'gov': 'Government',
      'amazon': 'Amazon',
      'discord': 'Discord',
    };
    
    for (const [key, value] of Object.entries(sourceMap)) {
      if (domain.includes(key)) {
        return value;
      }
    }
    
    return domain.charAt(0).toUpperCase() + domain.slice(1);
  }
  
  // Fallback to email prefix
  const emailMatch = from.match(/^(.+?)\s*<.+>$/);
  if (emailMatch) {
    return emailMatch[1].substring(0, 15);
  }
  
  return from.substring(0, 15);
}

function getSourceIcon(from: string): string {
  const domain = from.toLowerCase();
  
  if (domain.includes('gmail')) return '📧';
  if (domain.includes('outlook') || domain.includes('microsoft')) return '💼';
  if (domain.includes('apple')) return '🍎';
  if (domain.includes('southeast')) return '💧';
  if (domain.includes('amazon')) return '📦';
  if (domain.includes('discord')) return '💬';
  if (domain.includes('slack')) return '💙';
  if (domain.includes('twitter') || domain.includes('x.com')) return '𝕏';
  if (domain.includes('github')) return '🐙';
  
  return '📬';
}
